import { useTranslation } from '@rhino-project/core/i18n';
import { useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Button,
  Chip,
  Input,
  TextField,
  toast,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableContent,
  TableHeader,
  TableRow,
  TableScrollContainer
} from '@heroui/react';
import { Icon } from '@iconify/react';
import {
  useHasRoleOf,
  useModelCreate,
  useModelIndex,
  useModelUpdate
} from '@rhino-project/core/hooks';
import { BaseAuthedPage, MaxWidth } from '@rhino-project/ui-heroui';
import { usePreviewPortal } from '../../hooks/usePreviewPortal';
import type { RhinoRead } from '@rhino-project/core';

// The portal signs players in with Google OAuth, so only a Gmail address on
// the player contact actually grants access
const isGmail = (email: string) => email.toLowerCase().endsWith('@gmail.com');

const firstErrorMessage = (error: unknown) => {
  const errors = (error as { errors?: string[] | Record<string, string[]> })
    ?.errors;
  if (Array.isArray(errors)) return errors[0];
  if (errors) return Object.values(errors).flat()[0];
  return undefined;
};

const PortalAccessPage = () => {
  const { t } = useTranslation();

  const { team_season_id } = useParams({ strict: false });
  const isAdmin = useHasRoleOf('admin');
  const { openPreview, isPending: isPreviewPending } =
    usePreviewPortal(team_season_id);

  const activeRosterFilter = useMemo(
    () => ({
      rosters: { team_season_id, roster_status: { status_type: 'active' } }
    }),
    [team_season_id]
  );

  const { resources: players, isLoading } = useModelIndex('player', {
    filter: activeRosterFilter,
    order: ['last_name', 'first_name'],
    limit: 1000
  });

  const { resources: contacts } = useModelIndex('contact', {
    filter: { player_contact: true, player: activeRosterFilter },
    order: ['-updated_at'],
    limit: 1000
  });

  // The player contact carries the portal sign-in email. A player can have
  // more than one, so the most recently touched one wins.
  const contactsByPlayer = useMemo(() => {
    const map = new Map<number, RhinoRead<'contact'>>();
    (contacts?.results ?? []).forEach((contact) => {
      const playerId =
        typeof contact.player === 'object'
          ? contact.player?.id
          : contact.player;
      if (playerId != null && !map.has(playerId)) map.set(playerId, contact);
    });
    return map;
  }, [contacts]);

  const [editing, setEditing] = useState<{
    playerId: number;
    value: string;
  } | null>(null);

  const { mutate: createContact, isPending: isCreating } =
    useModelCreate('contact');
  const { mutate: updateContact, isPending: isUpdating } =
    useModelUpdate('contact');
  const isSaving = isCreating || isUpdating;

  const handleSave = (player: RhinoRead<'player'>) => {
    if (!editing) return;
    const email = editing.value.trim();
    const contact = contactsByPlayer.get(player.id);

    const options = {
      onSuccess: () => {
        setEditing(null);
        toast.success(
          t('frontend.messages.portal_email_saved', {
            name: player.display_name
          })
        );
      },
      onError: (error: unknown) => {
        toast.danger(
          firstErrorMessage(error) ??
            t('frontend.messages.could_not_save_the_portal_email')
        );
      }
    };

    if (contact) {
      updateContact({ id: contact.id, email: email || null } as never, options);
    } else if (email) {
      createContact(
        { player: player.id, player_contact: true, email } as never,
        options
      );
    } else {
      setEditing(null);
    }
  };

  const playerRows = players?.results ?? [];

  return (
    <BaseAuthedPage>
      <MaxWidth>
        <div className="flex justify-between items-center mb-2">
          <Typography.Heading level={1}>
            {t('frontend.messages.portal')}
          </Typography.Heading>
          <Button
            variant="primary"
            onPress={openPreview}
            isDisabled={isPreviewPending}
          >
            <Icon icon="bi:eye" />
            {t('frontend.messages.preview_portal')}
          </Button>
        </div>
        <Typography.Paragraph color="muted" className="mb-6">
          {t(
            'frontend.messages.players_sign_in_to_the_player_portal_with_google_using_the'
          )}
        </Typography.Paragraph>
        {!isLoading && playerRows.length === 0 ? (
          <Typography.Paragraph color="muted">
            {t('frontend.messages.no_players_on_the_active_roster_yet')}
          </Typography.Paragraph>
        ) : (
          <Table>
            <TableScrollContainer>
              <TableContent aria-label={t('frontend.messages.portal_access')}>
                <TableHeader>
                  <TableColumn isRowHeader>
                    {t('frontend.messages.player')}
                  </TableColumn>
                  <TableColumn>
                    {t('frontend.messages.portal_email')}
                  </TableColumn>
                  <TableColumn>{t('frontend.messages.access')}</TableColumn>
                  {isAdmin && (
                    <TableColumn>
                      <span className="sr-only">
                        {t('frontend.messages.actions')}
                      </span>
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody>
                  {playerRows.map((player) => {
                    const contact = contactsByPlayer.get(player.id);
                    const email = (contact?.email as string | null) ?? '';
                    const isEditingRow = editing?.playerId === player.id;

                    return (
                      <TableRow key={player.id as number}>
                        <TableCell>{player.display_name as string}</TableCell>
                        <TableCell>
                          {isEditingRow ? (
                            <TextField
                              variant="secondary"
                              aria-label={t(
                                'frontend.messages.portal_email_for',
                                { name: player.display_name }
                              )}
                              value={editing?.value ?? ''}
                              onChange={(value) =>
                                setEditing({
                                  playerId: player.id as number,
                                  value
                                })
                              }
                            >
                              <Input
                                type="email"
                                autoFocus
                                placeholder="@gmail.com"
                                onKeyDown={(e) => {
                                  // Keep keystrokes out of the table's
                                  // typeahead/arrow-key navigation, which
                                  // would steal focus to a matching row
                                  e.stopPropagation();
                                  if (e.key === 'Enter') handleSave(player);
                                  if (e.key === 'Escape') setEditing(null);
                                }}
                              />
                            </TextField>
                          ) : email ? (
                            email
                          ) : (
                            <span className="text-muted">
                              {t('frontend.messages.no_email')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {email && isGmail(email) ? (
                            <Chip color="success">
                              {t('frontend.messages.has_access')}
                            </Chip>
                          ) : email ? (
                            <Chip color="warning">
                              {t('frontend.messages.not_gmail')}
                            </Chip>
                          ) : (
                            <Chip color="default">
                              {t('frontend.messages.no_access')}
                            </Chip>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            {isEditingRow ? (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="primary"
                                  isDisabled={isSaving}
                                  onPress={() => handleSave(player)}
                                >
                                  {t('frontend.messages.save')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onPress={() => setEditing(null)}
                                >
                                  {t('frontend.messages.cancel')}
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onPress={() =>
                                  setEditing({
                                    playerId: player.id as number,
                                    value: email
                                  })
                                }
                              >
                                <Icon icon="bi:pencil" />
                                {email
                                  ? t('frontend.messages.edit')
                                  : t('frontend.messages.add_email')}
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </TableContent>
            </TableScrollContainer>
          </Table>
        )}
      </MaxWidth>
    </BaseAuthedPage>
  );
};

export default PortalAccessPage;
